import { useAuth } from 'oidc-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { hasSignInAnswer } from 'src/config/oidc';

const ATTEMPT_KEY = 'lmnt.silentSsoAttempted';
const SILENT_TIMEOUT_SECONDS = 5;

/** false while the login state of this tab is still being found out (oidc loading or the silent SSO check). */
export const SilentSsoContext = React.createContext(true);

export const useAuthSettled = (): boolean => useContext(SilentSsoContext);

/** One attempt per tab — a guest, a failed check or a logout in this tab never triggers another one. */
function claimAttempt(): boolean {
    try {
        if (window.sessionStorage.getItem(ATTEMPT_KEY)) return false;
        window.sessionStorage.setItem(ATTEMPT_KEY, '1');
        return true;
    } catch {
        return false;
    }
}

/**
 * Live-Runde 13 (Z-6): a tab that starts without a stored login (a link opened in a new tab) asks Keycloak
 * silently (iframe, prompt=none) for its SSO session; without one the visitor stays a guest at once.
 */
export const SilentSsoProvider: React.FC = ({ children }) => {
    const { isLoading, userData, userManager } = useAuth();
    const [checking, setChecking] = useState(() => !hasSignInAnswer(window.location));
    const started = useRef(false);

    useEffect(() => {
        if (!checking || isLoading || started.current) return;
        started.current = true;
        if (userData || !claimAttempt()) {
            setChecking(false);
            return;
        }
        const done = () => setChecking(false);
        // login_required and every other failure: the visitor stays a guest
        userManager.signinSilent({ silentRequestTimeoutInSeconds: SILENT_TIMEOUT_SECONDS }).then(done, done);
    }, [checking, isLoading, userData, userManager]);

    return <SilentSsoContext.Provider value={!isLoading && !checking}>{children}</SilentSsoContext.Provider>;
};
